import React from 'react';
import { useTranslation } from 'react-i18next';
import { ControlWrapper, CustomBackdrop, InputSelect } from '../../../../../../components';
import { CheckboxSelect } from '../../../../../../components/checkbox-select';
import { RangeHeader } from '../../../../../../components/range-header';
import { MeasurementRange } from '../../../../../../types';
import { ChannelType } from '../../../../types';
import { PhasesButtonGroup } from '../../measurements/components/phases-button-group';
import { EmptyPrices } from '../common/components/empty-prices/empty-prices';
import { useMeterPrices } from '../common/hooks/use-meter-prices';
import { AnalysisChart } from './components/chart';
import { CsvExport } from './components/csv-export/csv-export';
import { Summary } from './components/summary';
import { useMeterAnalysisPersisted } from './hooks/use-meter-analysis-persisted';
import { priceAndCostParameters } from './utils/helpers';

type MeterAnalysisProps = {
  channel: ChannelType;
};

export const MeterAnalysis: React.FC<MeterAnalysisProps> = ({ channel }) => {
  const { t: t } = useTranslation('channel-details');
  const { t: tc } = useTranslation('common');
  const {
    selectedPhases,
    isLoading,
    chartData,
    currentRangeLabel,
    aggregatedParameters,
    activeMeasurementRange,
    page,
    noData,
    isStandalone,
    defaultPhases,
    error,
    fromTo,
    isPreviousData,
    selectedParameters,
    singlePhaseChoice,
    setSelectedPhases,
    onClickActiveMeasurementRange,
    setPage,
    handleSetAggregatedParameters,
  } = useMeterAnalysisPersisted({ channel });
  const { emptyPrices } = useMeterPrices(selectedPhases);

  const parametersAvailable = aggregatedParameters.filter((x) => !x.disabled).length > 0;

  const priceParameterSelected = !!selectedParameters.find((x) => priceAndCostParameters.includes(x.value));

  const timeRanges = [
    { value: MeasurementRange.DayInHours, label: t('dateAbbreviation.hours') },
    { value: MeasurementRange.MonthInDays, label: t('dateAbbreviation.days') },
    { value: MeasurementRange.WeekInDays, label: t('dateAbbreviation.weeks') },
    { value: MeasurementRange.YearInMonths, label: t('dateAbbreviation.months') },
    { value: MeasurementRange.TenYearsInYears, label: t('dateAbbreviation.years') },
  ];

  const itemPrefix = t(isStandalone ? 'phase' : 'outputShort');

  const phasesShouldVisible = defaultPhases.length > 2;

  return (
    <div className="meter-analysis">
      <ControlWrapper className="control-wrapper--column  control-wrapper--reverse p-l-24 p-r-24 p-b-16">
        {parametersAvailable ? (
          <div className="grid-list-24">
            <CheckboxSelect
              options={aggregatedParameters}
              onSave={handleSetAggregatedParameters}
              reverse
              multipleLabel={tc('parameters')}
            />
            <InputSelect
              options={timeRanges}
              value={activeMeasurementRange}
              onChange={onClickActiveMeasurementRange}
              reverse
              label={''}
            />
          </div>
        ) : null}
        {emptyPrices && priceParameterSelected && <EmptyPrices channelId={channel.id} />}
        <RangeHeader
          prevAvailable={isPreviousData}
          nextAvailable={page < 0}
          range={currentRangeLabel}
          setPage={setPage}
        />
        <AnalysisChart
          data={chartData}
          activeMeasurementRange={activeMeasurementRange}
          isLoading={isLoading}
          aggregatedParameters={aggregatedParameters}
          itemPrefix={itemPrefix}
          fromTo={fromTo}
          error={error}
          noData={noData}
        />
        {phasesShouldVisible ? (
          <PhasesButtonGroup
            selectedPhases={selectedPhases}
            onSelectedPhases={setSelectedPhases}
            defaultPhases={defaultPhases}
            itemPrefix={itemPrefix}
            singleChoice={singlePhaseChoice}
          />
        ) : null}
        <CsvExport meterId={channel.id} />
      </ControlWrapper>
      <Summary summaryData={chartData} activeMeasurementRange={activeMeasurementRange} itemPrefix={itemPrefix} />
      <CustomBackdrop loading={isLoading && !chartData} />
    </div>
  );
};
